<template>
  <div :class="$style.container">
    <!-- 헤더 -->
    <div :class="$style.header">
      <div :class="$style.headerLeft">
        <button :class="$style.backButton" @click="goBack" title="프로필로 돌아가기">←</button>
        <h2 :class="$style.title">예매 내역</h2>
      </div>
      <div :class="$style.headerRight">
        <span v-if="!loading" :class="$style.count">{{ reservations.length }}건</span>
        <div v-else :class="$style.spacer"></div>
      </div>
    </div>

    <div :class="$style.paper">
      <!-- 로딩 -->
      <div v-if="loading" :class="$style.loading">
        <div :class="$style.spinner"></div>
        <p>예매 내역을 불러오는 중...</p>
      </div>

      <!-- 에러 -->
      <div v-else-if="error" :class="$style.error">
        <p>{{ error }}</p>
        <button :class="$style.retryButton" @click="loadReservations">다시 시도</button>
      </div>

      <!-- 목록 -->
      <div v-else-if="reservations.length > 0" :class="$style.list">
        <div v-for="r in reservations" :key="r.id" :class="$style.card">
          <div :class="$style.ticket" @click="toggleExpand(r)">
            <div :class="$style.ticketStub">
              <div :class="$style.stubPoster" :style="getStubStyle(r)"></div>
              <div v-if="!getPosterUrl(r)" :class="$style.stubNoPoster">포스터</div>
              <div :class="$style.stubInfoOverlay">
                <div :class="$style.stubLabel">TICKET</div>
                <div :class="$style.stubQty">{{ r.quantity }}매</div>
              </div>
            </div>
            <div :class="$style.ticketBody">
              <div :class="$style.ticketHeaderRow">
                <div :class="$style.liveTitle" :title="getLiveTitle(r)">
                  {{ getLiveTitle(r) }}
                </div>
                <span :class="$style.expandIcon">{{ isExpanded(r.id) ? "▲" : "▼" }}</span>
              </div>

              <div :class="$style.ticketInfoRow">
                <span v-if="getLiveDate(r)" :class="$style.metaText">{{ getLiveDate(r) }}</span>
                <span v-if="getLiveDate(r) && getLivePlace(r)" :class="$style.dot">·</span>
                <span v-if="getLivePlace(r)" :class="$style.metaText">{{ getLivePlace(r) }}</span>
              </div>

              <div :class="$style.badges">
                <span v-if="r.isInviteTicket" :class="$style.inviteBadge">🎟️ 초대권</span>
                <span v-else :class="[$style.statusBadge, $style[getUnifiedStatusKey(r)]]">
                  {{ getUnifiedStatusText(r) }}
                </span>
              </div>

              <div
                v-if="isRefundPending(r) && !r.isInviteTicket"
                :class="$style.refundExpectedRow"
              >
                <span :class="$style.refundExpectedLabel">환불 예정</span>
                <span :class="$style.refundExpectedValue">{{ getExpectedRefundAmountDisplay(r) }}</span>
              </div>

              <div :class="$style.ticketFooterRow">
                <span :class="$style.metaText">신청 {{ formatDate(r.createdAt) }}</span>
                <span :class="$style.ticketNo">#{{ getTicketNo(r.id) }}</span>
              </div>
            </div>
          </div>

          <div v-if="isExpanded(r.id)" :class="$style.detail">

            <!-- 초대권: QR만 표시 -->
            <template v-if="r.isInviteTicket">
              <div :class="$style.inviteTicketNotice">
                🎟️ 초대권으로 발급된 티켓입니다.
              </div>
              <div :class="$style.detailRow">
                <span :class="$style.detailLabel">티켓번호</span>
                <span :class="$style.detailValue">#{{ getTicketNo(r.id) }}</span>
              </div>
              <div v-if="r.liveId" :class="$style.detailRow">
                <span :class="$style.detailLabel">공연 페이지</span>
                <button :class="$style.linkButton" @click="goToLive(r.liveId)">바로가기</button>
              </div>
              <div :class="$style.dividerTitle">
                <span>QR 코드</span>
              </div>
              <div :class="$style.qrBox">
                <div v-if="qrLoadingMap[r.id]" :class="$style.qrLoading">QR 생성 중...</div>
                <div v-else-if="qrErrorMap[r.id]" :class="$style.qrError">{{ qrErrorMap[r.id] }}</div>
                <img v-else-if="qrMap[r.id]" :src="qrMap[r.id]" alt="QR 코드" :class="$style.qrImage" />
                <div :class="$style.qrHint">현장에서 QR 확인이 필요할 때 사용하세요.</div>
              </div>
            </template>

            <!-- 일반 티켓: 기존 상세 정보 표시 -->
            <template v-else>
              <div :class="$style.detailRow">
                <span :class="$style.detailLabel">예매자명</span>
                <span :class="$style.detailValue">{{ r.name || "-" }}</span>
              </div>
              <div :class="$style.detailRow">
                <span :class="$style.detailLabel">티켓번호</span>
                <span :class="$style.detailValue">#{{ getTicketNo(r.id) }}</span>
              </div>
              <div :class="$style.detailRow">
                <span :class="$style.detailLabel">구매 매수</span>
                <span :class="$style.detailValue">{{ r.quantity }}매</span>
              </div>
              <div v-if="!r.isInviteTicket && r.totalAmount" :class="$style.detailRow">
                <span :class="$style.detailLabel">예매 금액</span>
                <span :class="$style.detailValue">{{ formatAmount(r.totalAmount) }}</span>
              </div>
              <div v-if="isRefundPending(r) && !r.isInviteTicket" :class="$style.detailRow">
                <span :class="$style.detailLabel">환불 예정 금액</span>
                <span :class="[$style.detailValue, $style.refundExpectedDetailValue]">{{
                  getExpectedRefundAmountDisplay(r)
                }}</span>
              </div>
              <p v-if="isRefundPending(r) && !r.isInviteTicket" :class="$style.refundExpectedHint">
                링꾹라이브 기본 환불·취소 규정(공연일 기준)으로 산정한 금액입니다. 주최자별 규정이 있으면 최종 환불액은 달라질 수 있습니다.
              </p>
              <div v-if="r.liveId" :class="$style.detailRow">
                <span :class="$style.detailLabel">공연 페이지</span>
                <button :class="$style.linkButton" @click="goToLive(r.liveId)">바로가기</button>
              </div>

              <div v-if="getCustomAnswers(r).length" :class="$style.dividerTitle">
                <span>추가 정보</span>
              </div>
              <div v-for="(a, idx) in getCustomAnswers(r)" :key="idx" :class="$style.detailRow">
                <span :class="$style.detailLabel">{{ a.question }}</span>
                <span :class="$style.detailValue">{{ a.answer }}</span>
              </div>

              <!-- 티켓 패키지 선택 내역 -->
              <template v-if="getRewardSummary(r).length">
                <div :class="$style.dividerTitle">
                  <span>티켓 패키지</span>
                </div>
                <div v-for="(rw, idx) in getRewardSummary(r)" :key="'reward-' + idx" :class="$style.detailRow">
                  <span :class="$style.detailLabel">{{ rw.name }}</span>
                  <span :class="$style.detailValue">{{ rw.quantity }}개 · {{ rw.subtotal }}</span>
                </div>
                <div v-if="!r.isInviteTicket && r.rewardTotal" :class="[$style.detailRow, $style.detailRowTotal]">
                  <span :class="$style.detailLabel">패키지 합계</span>
                  <span :class="[$style.detailValue, $style.detailValueTotal]">{{ formatAmount(r.rewardTotal) }}</span>
                </div>
              </template>

              <!-- 굿즈 구매 내역 -->
              <template v-if="getPurchaseSummary(r).length">
                <div :class="$style.dividerTitle">
                  <span>추가구매항목</span>
                </div>
                <div v-for="(g, idx) in getPurchaseSummary(r)" :key="'goods-' + idx" :class="$style.detailRow">
                  <span :class="$style.detailLabel">{{ g.name }}</span>
                  <span :class="$style.detailValue">
                    {{ g.quantity }}개 · {{ g.subtotal }}
                    <span v-if="g.optionAnswer" :class="$style.goodsOption">({{ g.optionAnswer }})</span>
                  </span>
                </div>
              <div v-if="!r.isInviteTicket && r.purchaseTotal" :class="[$style.detailRow, $style.detailRowTotal]">
                  <span :class="$style.detailLabel">추가구매 합계</span>
                  <span :class="[$style.detailValue, $style.detailValueTotal]">{{ formatAmount(r.purchaseTotal) }}</span>
                </div>
              </template>

              <div :class="$style.dividerTitle">
                <span>QR 코드</span>
              </div>
              <div :class="$style.qrBox">
                <div v-if="qrLoadingMap[r.id]" :class="$style.qrLoading">QR 생성 중...</div>
                <div v-else-if="qrErrorMap[r.id]" :class="$style.qrError">{{ qrErrorMap[r.id] }}</div>
                <img v-else-if="qrMap[r.id]" :src="qrMap[r.id]" alt="QR 코드" :class="$style.qrImage" />
                <div :class="$style.qrHint">현장에서 QR 확인이 필요할 때 사용하세요.</div>
              </div>

              <div :class="$style.detailActions">
                <!-- 환불 완료 -->
                <template v-if="r.status === 'refund_completed'">
                  <div :class="$style.refundCompletedBadge">환불 완료</div>
                </template>

                <!-- 취소 전: 예매취소 버튼만 -->
                <template v-else-if="r.status !== 'cancelled' && r.status !== 'refund_requested'">
                  <button :class="$style.cancelButton" @click.stop="cancelReservation(r)"
                    :disabled="cancelLoadingMap[r.id]">
                    {{ cancelLoadingMap[r.id] ? "취소 중..." : "예매취소" }}
                  </button>
                  <div :class="$style.cancelHint">취소 후에는 되돌릴 수 없습니다.</div>
                </template>

                <!-- 취소 후 / 환불요청 상태: 환불 요청 영역 -->
                <template v-else>
                  <!-- 취소됨 + 환불 요청 완료 -->
                  <template v-if="r.status === 'refund_requested' || r.refundRequested">
                    <div :class="$style.statusBadgeRow">
                      <div :class="$style.cancelledBadge">취소됨</div>
                      <div :class="$style.refundRequestedBadge">환불 요청 접수됨 · 주최자 확인 중</div>
                    </div>
                  </template>

                  <!-- 입금 완료된 티켓: 환불 요청 가능 -->
                  <template v-else-if="r.paymentConfirmed && refundFormOpenId !== r.id">
                    <div :class="$style.cancelledBadge">취소됨</div>
                    <button :class="$style.refundButton" @click.stop="openRefundForm(r.id)">
                      환불 요청하기
                    </button>
                  </template>

                  <!-- 입금대기 티켓: 취소됨만 표시 -->
                  <template v-else-if="!r.paymentConfirmed && refundFormOpenId !== r.id">
                    <div :class="$style.cancelledBadge">취소됨</div>
                  </template>

                  <!-- 환불 계좌 입력 폼 -->
                  <div v-else :class="$style.refundForm" @click.stop>
                    <p :class="$style.refundFormTitle">환불받을 계좌를 입력해 주세요</p>
                    <p :class="$style.refundFormNotice">{{ getRefundRequesterNotice(r) }}</p>
                    <div :class="$style.refundFormRow">
                      <label :class="$style.refundFormLabel">은행명</label>
                      <input :class="$style.refundFormInput" v-model="refundFormData.bankName" placeholder="예: 카카오뱅크"
                        :disabled="refundRequestLoadingMap[r.id]" />
                    </div>
                    <div :class="$style.refundFormRow">
                      <label :class="$style.refundFormLabel">계좌번호</label>
                      <input :class="$style.refundFormInput" v-model="refundFormData.accountNumber"
                        placeholder="예: 3333-01-1234567" :disabled="refundRequestLoadingMap[r.id]" />
                    </div>
                    <div :class="$style.refundFormRow">
                      <label :class="$style.refundFormLabel">예금주</label>
                      <input :class="$style.refundFormInput" v-model="refundFormData.accountHolder" placeholder="예금주명"
                        :disabled="refundRequestLoadingMap[r.id]" />
                    </div>
                    <div :class="$style.refundFormActions">
                      <button :class="$style.refundFormCancelBtn" @click.stop="closeRefundForm"
                        :disabled="refundRequestLoadingMap[r.id]">
                        닫기
                      </button>
                      <button :class="$style.refundFormSubmitBtn" @click.stop="requestRefund(r)"
                        :disabled="refundRequestLoadingMap[r.id] || !refundFormData.bankName || !refundFormData.accountNumber || !refundFormData.accountHolder">
                        {{ refundRequestLoadingMap[r.id] ? "요청 중..." : "요청 제출" }}
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 빈 상태 -->
      <div v-else :class="$style.emptyState">
        <div :class="$style.emptyIcon">🎟️</div>
        <h3>예매 내역이 없습니다</h3>
        <p>공연 상세 페이지에서 예매를 진행해보세요.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from "@/store/user";
import { firestore, storage } from "/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import QRCode from "qrcode";
import { updateReservationStatus } from "@/utils/write/ticket";
import { getMyTicketReservations, createdAtToSeconds } from "@/utils/read/ticket";
import { createNotification } from "@/utils/write/alarm";

export default {
  name: "MyReservations",
  data() {
    return {
      userStore: useUserStore(),
      loading: true,
      error: null,
      reservations: [],
      expanded: new Set(),
      liveMap: {}, // liveId -> { title, date, place }
      formMap: {}, // liveId -> { customFields: [...] }
      qrMap: {}, // reservationId -> dataUrl
      qrLoadingMap: {}, // reservationId -> boolean
      qrErrorMap: {}, // reservationId -> string
      cancelLoadingMap: {}, // reservationId -> boolean
      refundRequestLoadingMap: {}, // reservationId -> boolean
      refundFormOpenId: null, // 환불 폼이 열린 예매 ID
      refundFormData: { bankName: "", accountNumber: "", accountHolder: "" },
    };
  },
  async created() {
    if (!this.userStore.isLogin) {
      this.$alert("로그인이 필요합니다");
      this.$router.push("/");
      return;
    }
    await this.loadReservations();
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    toggleExpand(reservation) {
      const id = reservation?.id;
      if (!id) return;
      const next = new Set(this.expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.expanded = next;

      // 펼칠 때 QR 생성 (캐싱)
      if (this.expanded.has(id)) {
        this.ensureQrForReservation(reservation);
      }
    },
    isExpanded(id) {
      return this.expanded.has(id);
    },
    getStatusText(status) {
      const map = {
        pending: "대기중",
        confirmed: "확정",
        cancelled: "취소",
        refund_requested: "환불 요청",
        refund_completed: "환불 완료",
      };
      return map[status] || status || "-";
    },
    getUnifiedStatusKey(r) {
      if (r.status === "refund_completed") return "refund_completed";
      if (r.status === "refund_requested") return "refund_requested";
      if (r.status === "cancelled") return "cancelled";
      if (r.status === "confirmed") return "confirmed";
      if (r.paymentConfirmed) return "pay_confirmed";
      return "pay_pending";
    },
    getUnifiedStatusText(r) {
      if (r.status === "refund_completed") return "환불 완료";
      if (r.status === "refund_requested") return "환불 대기중";
      if (r.status === "cancelled") return "취소됨";
      if (r.status === "confirmed") return "예매확정";
      if (r.paymentConfirmed) return "입금완료";
      return "입금 확인중";
    },
    formatDate(ts) {
      try {
        const seconds = createdAtToSeconds(ts);
        if (!seconds) return "-";
        const d = new Date(seconds * 1000);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
      } catch (e) {
        return "-";
      }
    },
    getLiveTitle(r) {
      return (
        r.liveTitle ||
        this.liveMap?.[r.liveId]?.title ||
        "공연"
      );
    },
    getLiveDate(r) {
      return r?.liveDate || this.liveMap?.[r.liveId]?.date || "";
    },
    getLivePlace(r) {
      return r?.livePlace || this.liveMap?.[r.liveId]?.place || "";
    },
    getPosterUrl(r) {
      const url = r?.liveImg || r?.poster || this.liveMap?.[r.liveId]?.img || "";
      if (!url) return "";
      // 다운로드 URL이 아니면(=파일명 등) 표시하지 않음
      if (typeof url === "string" && !url.startsWith("http")) return "";
      return url;
    },
    getStubStyle(r) {
      const url = this.getPosterUrl(r);
      if (!url) return {};
      return {
        backgroundImage: `url(${url})`,
      };
    },
    getTicketNo(id) {
      const str = String(id || "");
      if (!str) return "-";
      return str.slice(-8).toUpperCase();
    },
    getTicketLink(reservation) {
      // 기본은 "예매 확인" 페이지로 이동 (로그인 필요)
      const origin = window?.location?.origin || "";
      const liveId = reservation?.liveId || "";
      const rid = reservation?.id || "";
      if (!origin || !liveId) return "";
      // 추후 스캐너/검증 페이지가 생기면 여기만 교체하면 됨
      return `${origin}/ticket/${liveId}?mode=view&rid=${encodeURIComponent(rid)}`;
    },
    async ensureQrForReservation(reservation) {
      const id = reservation?.id;
      if (!id) return;
      if (this.qrMap?.[id]) return;
      if (this.qrLoadingMap?.[id]) return;

      const qrValue =
        reservation?.qrValue ||
        this.getTicketLink(reservation) ||
        JSON.stringify({
          reservationId: id,
          liveId: reservation?.liveId || null,
          userId: reservation?.userId || null,
          email: reservation?.email || null,
        });

      this.qrLoadingMap = { ...this.qrLoadingMap, [id]: true };
      this.qrErrorMap = { ...this.qrErrorMap, [id]: "" };
      try {
        const dataUrl = await QRCode.toDataURL(qrValue, {
          width: 220,
          margin: 1,
          errorCorrectionLevel: "M",
          color: { dark: "#1f2937", light: "#ffffff" },
        });
        this.qrMap = { ...this.qrMap, [id]: dataUrl };
      } catch (e) {
        console.error("QR 생성 오류:", e);
        this.qrErrorMap = { ...this.qrErrorMap, [id]: "QR 코드를 생성하지 못했습니다." };
      } finally {
        this.qrLoadingMap = { ...this.qrLoadingMap, [id]: false };
      }
    },
    async goToLive(liveId) {
      if (!liveId) return;
      this.$router.push(`/live/${liveId}`);
    },
    openTicketLink(reservation) {
      const url = this.getTicketLink(reservation);
      if (!url) return;
      window.open(url, "_blank");
    },
    async cancelReservation(reservation) {
      const id = reservation?.id;
      if (!id) return;
      if (reservation?.isInviteTicket) return;
      if (reservation?.status === "cancelled") return;
      if (this.cancelLoadingMap?.[id]) return;

      const ok = await this.$confirm("예매를 취소하시겠습니까?\n취소 후에는 되돌릴 수 없습니다.");
      if (!ok) return;

      this.cancelLoadingMap = { ...this.cancelLoadingMap, [id]: true };
      try {
        await updateReservationStatus(id, "cancelled");
        this.$alert("예매가 취소되었습니다.");
        await this.loadReservations();
      } catch (e) {
        console.error("예매 취소 오류:", e);
        this.$alert("예매 취소 중 오류가 발생했습니다.");
      } finally {
        this.cancelLoadingMap = { ...this.cancelLoadingMap, [id]: false };
      }
    },
    openRefundForm(id) {
      this.refundFormOpenId = id;
      this.refundFormData = { bankName: "", accountNumber: "", accountHolder: "" };
    },
    closeRefundForm() {
      this.refundFormOpenId = null;
      this.refundFormData = { bankName: "", accountNumber: "", accountHolder: "" };
    },
    getCancelPolicyText(reservation) {
      const liveId = reservation?.liveId;
      const policy = this.formMap?.[liveId]?.cancelPolicy;
      if (policy?.enabled && policy?.content) {
        return policy.content;
      }
      return "• 공연 시작일 3일 전까지: 결제 금액 전액 환불\n• 공연 시작일 2일 전: 결제 금액의 70% 환불\n• 공연 시작일 1일 전 및 당일 이후: 환불 불가\n\n";
    },
    /** 공연 일시 문자열·Timestamp → Date */
    parseLivePerformanceDate(raw) {
      if (raw == null) return null;
      if (typeof raw?.toDate === "function") {
        const d = raw.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
      }
      if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
      const s = String(raw).trim();
      const m = /^(\d{4})\.(\d{2})\.(\d{2})(?:\s+(\d{2}):(\d{2}))?/.exec(s);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]) - 1;
        const d = Number(m[3]);
        const hh = m[4] != null ? Number(m[4]) : 0;
        const mi = m[5] != null ? Number(m[5]) : 0;
        return new Date(y, mo, d, hh, mi, 0, 0);
      }
      const t = Date.parse(s);
      return Number.isFinite(t) ? new Date(t) : null;
    },
    /** 공연일 자정 vs 기준일 자정 — 공연 당일이면 0, 하루 전이면 1 … */
    getCalendarDaysBeforeShow(showDate, fromDate = new Date()) {
      if (!(showDate instanceof Date) || Number.isNaN(showDate.getTime())) return null;
      const showDay = new Date(showDate.getFullYear(), showDate.getMonth(), showDate.getDate());
      const fromDay = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
      return Math.round((showDay.getTime() - fromDay.getTime()) / 86400000);
    },
    /**
     * 링꾹 기본 규정(공연일 달력 기준): 3일 전 이상 100%, 2일 전 70%, 그 외 0%
     */
    getDefaultRefundRateByDays(daysBeforeShow) {
      if (daysBeforeShow == null || Number.isNaN(daysBeforeShow)) return { rate: null };
      if (daysBeforeShow >= 3) return { rate: 1 };
      if (daysBeforeShow === 2) return { rate: 0.7 };
      return { rate: 0 };
    },
    getReservationPaidTotal(reservation) {
      const total = Number(reservation?.totalAmount);
      if (Number.isFinite(total) && total > 0) return total;
      const q = Number(reservation?.quantity) || 0;
      const unit = Number(reservation?.ticketAmount) || 0;
      let ticket = q * unit;
      const purchase = Number(reservation?.purchaseTotal) || 0;
      let reward = Number(reservation?.rewardTotal) || 0;
      if ((!ticket || ticket <= 0) && Array.isArray(reservation?.rewardAnswers)) {
        const tiers = this.formMap?.[reservation.liveId]?.rewardTiers || [];
        reward = reservation.rewardAnswers.reduce((sum, a) => {
          const tier = tiers[a.tierIndex];
          const qty = Number(a?.quantity) || 0;
          const price = Number(tier?.price) || 0;
          return sum + price * qty;
        }, 0);
        if (!q && reward > 0) ticket = 0;
      }
      const sum = ticket + purchase + reward;
      return Number.isFinite(sum) && sum > 0 ? sum : 0;
    },
    /** 결제·공연일 기준 링꾹 기본 규정 환불액 산정. 불가 시 null */
    _computeRefundEstimate(reservation) {
      const paidTotal = this.getReservationPaidTotal(reservation);
      const dateRaw = this.getLiveDate(reservation) || this.liveMap?.[reservation.liveId]?.date;
      const showDate = this.parseLivePerformanceDate(dateRaw);
      if (!paidTotal || paidTotal <= 0 || !showDate) return null;
      const daysBefore = this.getCalendarDaysBeforeShow(showDate, new Date());
      const { rate } = this.getDefaultRefundRateByDays(daysBefore);
      if (rate == null) return null;
      const refundAmount = Math.floor(paidTotal * rate);
      return { paidTotal, daysBefore, rate, refundAmount };
    },
    /** 요청자 안내(폼 상단): 규정 + 예상 환불액 */
    getRefundRequesterNotice(reservation) {
      const lines = [
        "환불 처리는 링꾹라이브 기본 환불·취소 규정(공연 시작일 달력 기준)에 맞게 적용됩니다.",
        "• 공연 시작일 3일 전까지: 결제 금액 전액 환불",
        "• 공연 시작일 2일 전: 결제 금액의 70% 환불",
        "• 공연 시작일 1일 전 및 당일 이후: 환불 불가",
        "",
      ];
      const est = this._computeRefundEstimate(reservation);
      if (est) {
        lines.push(`예상 환불액: ${this.formatAmount(est.refundAmount)}`);
      } else {
        lines.push(
          "예상 환불액: 결제·공연 일정 정보로 자동 산정하지 못했습니다. 접수 후 주최자 안내를 기다려 주세요."
        );
      }
      const custom = this.formMap?.[reservation?.liveId]?.cancelPolicy;
      if (custom?.enabled && custom?.content) {
        lines.push("");
        lines.push("※ 이 공연에는 주최자별 취소·환불 규정이 있을 수 있으며, 해당 규정이 우선될 수 있습니다.");
      }
      return lines.join("\n");
    },
    /** 제출 전 확인 문구 */
    getRefundSubmitConfirmText(reservation) {
      const est = this._computeRefundEstimate(reservation);
      const amountPart = est
        ? `예상 환불액: ${this.formatAmount(est.refundAmount)}`
        : "예상 환불액은 여기서 표시하지 못했습니다. 주최자 안내를 참고해 주세요.";
      const custom = this.formMap?.[reservation?.liveId]?.cancelPolicy;
      const customPart =
        custom?.enabled && custom?.content
          ? "\n\n※ 주최자별 규정이 있으면 최종 환불액은 그 기준을 따를 수 있습니다."
          : "";
      return `링꾹라이브 기본 환불·취소 규정에 따라 처리됩니다.\n\n${amountPart}${customPart}\n\n환불 요청을 제출하시겠습니까?`;
    },
    /** 환불 요청 접수 후 티켓에 표시할 금액 문구 */
    isRefundPending(r) {
      return r?.status === "refund_requested" || r?.refundRequested;
    },
    getExpectedRefundAmountDisplay(reservation) {
      const est = this._computeRefundEstimate(reservation);
      if (est) return this.formatAmount(est.refundAmount);
      return "안내 예정";
    },
    /** 주최자 알림용 환불액 값만 (산정 불가 시 "-", 가능 시 formatAmount 문자열) */
    getRefundAmountNotificationValue(reservation) {
      const est = this._computeRefundEstimate(reservation);
      if (!est) return "-";
      return this.formatAmount(est.refundAmount);
    },
    async requestRefund(reservation) {
      const id = reservation?.id;
      if (!id) return;
      if (reservation?.status === "refund_requested" || reservation?.refundRequested) return;
      if (this.refundRequestLoadingMap?.[id]) return;

      const { bankName, accountNumber, accountHolder } = this.refundFormData;
      if (!bankName || !accountNumber || !accountHolder) return;

      const okSubmit = await this.$confirm(this.getRefundSubmitConfirmText(reservation));
      if (!okSubmit) return;

      const liveTitle = this.getLiveTitle(reservation);

      this.refundRequestLoadingMap = { ...this.refundRequestLoadingMap, [id]: true };
      try {
        await updateReservationStatus(id, "refund_requested");

        const organizerEmails = this.liveMap?.[reservation.liveId]?.organizerEmails || [];
        const userNickname = this.userStore.nickname || this.userStore.email || "예매자";
        const accountInfo = `${bankName} ${accountNumber} (${accountHolder})`;
        const refundAmount = this.getRefundAmountNotificationValue(reservation);

        await Promise.allSettled(
          organizerEmails.map((email) =>
            createNotification({
              userId: email,
              type: "refund_request",
              title: "환불 요청이 접수되었습니다",
              message:
                `[${liveTitle}] ${userNickname}님이 환불을 요청했습니다.\n` +
                `티켓번호: #${this.getTicketNo(id)}\n` +
                `환불 계좌: ${accountInfo}\n` +
                `환불액: ${refundAmount}`,
              targetType: "reservation",
              targetId: id,
              fromUserId: this.userStore.email,
              fromUserNickname: userNickname,
            })
          )
        );

        this.reservations = this.reservations.map((r) =>
          r.id === id ? { ...r, refundRequested: true, status: "refund_requested" } : r
        );
        this.closeRefundForm();
        this.$alert("환불 요청이 접수되었습니다.\n주최자 확인 후 안내드립니다.");
      } catch (e) {
        console.error("환불 요청 오류:", e);
        this.$alert("환불 요청 중 오류가 발생했습니다.");
      } finally {
        this.refundRequestLoadingMap = { ...this.refundRequestLoadingMap, [id]: false };
      }
    },
    getRewardSummary(reservation) {
      const liveId = reservation?.liveId;
      const tiers = this.formMap?.[liveId]?.rewardTiers || [];
      const answers = reservation?.rewardAnswers;
      if (!Array.isArray(answers) || !tiers.length) return [];
      return answers
        .map(a => {
          const tier = tiers[a.tierIndex];
          if (!tier || !a.quantity) return null;
          return {
            name: tier.name,
            quantity: a.quantity,
            subtotal: this.formatAmount((Number(tier.price) || 0) * a.quantity),
          };
        })
        .filter(Boolean);
    },
    getPurchaseSummary(reservation) {
      const liveId = reservation?.liveId;
      const items = this.formMap?.[liveId]?.purchaseItems || [];
      const answers = reservation?.purchaseAnswers;
      if (!Array.isArray(answers) || !items.length) return [];

      return items
        .map((item, i) => {
          const ans = answers[i];
          const qty = ans?.quantity || 0;
          if (!qty) return null;
          const subtotal = this.formatAmount((Number(item.price) || 0) * qty);
          let optionAnswer = null;
          if (ans?.optionAnswer) {
            optionAnswer = Array.isArray(ans.optionAnswer)
              ? ans.optionAnswer.join(", ")
              : String(ans.optionAnswer);
          }
          return { name: item.name, quantity: qty, subtotal, optionAnswer };
        })
        .filter(Boolean);
    },
    formatAmount(amount) {
      if (typeof amount === 'number') {
        return `${new Intl.NumberFormat('ko-KR').format(amount)}원`;
      }
      // 이미 포맷된 문자열이면 그대로 반환
      return amount || '-';
    },
    getCustomAnswers(reservation) {
      const liveId = reservation?.liveId || null;
      const fields =
        reservation?.customFields ||
        this.formMap?.[liveId]?.customFields ||
        null;

      const answers = reservation?.customFieldAnswers || {};

      // 키 정규화: 숫자 인덱스 기반 정렬
      const keys = Array.isArray(answers)
        ? answers.map((_, i) => i)
        : Object.keys(answers)
          .map((k) => (String(Number(k)) === String(k) ? Number(k) : k))
          .sort((a, b) => {
            const aNum = typeof a === "number";
            const bNum = typeof b === "number";
            if (aNum && bNum) return a - b;
            if (aNum) return -1;
            if (bNum) return 1;
            return String(a).localeCompare(String(b));
          });

      const normalized = keys
        .map((k) => {
          const value = Array.isArray(answers) ? answers[k] : answers[k];
          const display =
            Array.isArray(value) ? value.join(", ") : value ?? "";
          return {
            question:
              (typeof k === "number" ? fields?.[k]?.title : null) ||
              (typeof k === "string" ? fields?.[Number(k)]?.title : null) ||
              `항목 ${typeof k === "number" ? k + 1 : String(k)}`,
            answer: display === "" ? "-" : String(display),
          };
        })
        .filter((a) => a.answer && a.answer !== "-");

      return normalized;
    },
    async loadReservations() {
      this.loading = true;
      this.error = null;
      try {
        const email = this.userStore.email;
        if (!email) {
          this.reservations = [];
          this.loading = false;
          return;
        }

        const list = await getMyTicketReservations(email);
        this.reservations = list;

        // liveId 기준으로 공연 정보/포스터 보강 (title이 있어도 포스터/일시/장소가 필요함)
        const needIds = Array.from(
          new Set(
            list
              .filter((r) => r.liveId && !this.liveMap?.[r.liveId])
              .map((r) => r.liveId)
          )
        );
        if (needIds.length) {
          const entries = await Promise.all(
            needIds.map(async (id) => {
              try {
                const s = await getDoc(doc(firestore, "lives", id));
                if (!s.exists()) return [id, null];
                const data = s.data() || {};

                // 포스터 URL 변환 (img 또는 poster)
                let imgUrl = data.img || data.poster || null;
                if (imgUrl && typeof imgUrl === "string" && !imgUrl.startsWith("http")) {
                  try {
                    // img가 "poster/xxx" 형태일 수도 있어서 정규화
                    const normalizedPath = imgUrl.includes("/") ? imgUrl : `poster/${imgUrl}`;
                    const imageRef = storageRef(storage, normalizedPath);
                    imgUrl = await getDownloadURL(imageRef);
                  } catch (e) {
                    imgUrl = null;
                  }
                }

                // organizer 정규화 (배열 또는 문자열)
                let organizerEmails = [];
                if (data.organizer) {
                  organizerEmails = Array.isArray(data.organizer)
                    ? data.organizer
                    : [data.organizer];
                }
                if (data.createdBy && !organizerEmails.includes(data.createdBy)) {
                  organizerEmails.push(data.createdBy);
                }

                return [
                  id,
                  {
                    title: data.title || "공연",
                    date: data.date || null,
                    place: data.place || null,
                    img: imgUrl || null,
                    organizerEmails,
                  },
                ];
              } catch (e) {
                return [id, null];
              }
            })
          );
          const nextMap = { ...this.liveMap };
          entries.forEach(([id, v]) => {
            if (v) nextMap[id] = v;
          });
          this.liveMap = nextMap;
        }

        // liveId 기준으로 ticketForm(질문지) 보강: customFields 제목 표시용
        const needFormIds = Array.from(
          new Set(
            list
              .filter((r) => r.liveId && !this.formMap?.[r.liveId])
              .map((r) => r.liveId)
          )
        );
        if (needFormIds.length) {
          const formEntries = await Promise.all(
            needFormIds.map(async (liveId) => {
              try {
                const formSnap = await getDoc(doc(firestore, "ticketForm", liveId));
                if (!formSnap.exists()) return [liveId, null];
                const data = formSnap.data() || {};
                return [liveId, {
                  customFields: Array.isArray(data.customFields) ? data.customFields : [],
                  cancelPolicy: data.cancelPolicy || null,
                  purchaseItems: Array.isArray(data.purchaseItems) ? data.purchaseItems : [],
                  rewardTiers: Array.isArray(data.rewardTiers) ? data.rewardTiers : [],
                }];
              } catch (e) {
                return [liveId, null];
              }
            })
          );
          const nextFormMap = { ...this.formMap };
          formEntries.forEach(([id, v]) => {
            if (v) nextFormMap[id] = v;
          });
          this.formMap = nextFormMap;
        }
      } catch (e) {
        console.error(e);
        this.error = "예매 내역을 불러오는 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style module>
.container {
  width: 90%;
  min-height: 100vh;
  margin: 0 auto;
  padding: 0;
}

.paper {
  background: white;
  padding: 16px 20px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 12px 0 4px;
  border-bottom: 1px solid #efefef;
  flex-shrink: 0;
}

.headerLeft {
  display: flex;
  align-items: center;
  gap: 4px;
}

.backButton {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #333;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background 0.15s;
}

.backButton:hover {
  background: #f5f5f5;
}

.title {
  font-size: 19px;
  font-weight: 700;
  color: #111;
  margin: 0;
  font-family: "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.3px;
}

.headerRight {
  display: flex;
  align-items: center;
  gap: 8px;
}

.count {
  font-size: 13px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-right: 10px;
}

.spacer {
  width: 30px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.error {
  text-align: center;
  padding: 30px 10px;
  color: #c62828;
  background: rgba(231, 76, 60, 0.08);
  border: 1px solid rgba(231, 76, 60, 0.18);
  border-radius: 12px;
}

.retryButton {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  background: #fff;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
}

.retryButton:hover {
  border-color: #66bdff;
  background: rgba(102, 189, 255, 0.12);
}

.list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

/* 티켓 카드 */
.ticket {
  display: flex;
  align-items: stretch;
  cursor: pointer;
}

.ticket:hover {
  background: #f8f9fa;
}

.ticketStub {
  width: 110px;
  background: #f1f3f5;
  border-right: 1px dashed rgba(102, 189, 255, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
}

.stubPoster {
  width: 100%;
  height: 100%;
  min-height: 150px;
  background: #f1f3f5;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.ticketStub::before,
.ticketStub::after {
  content: "";
  position: absolute;
  right: -10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #e9ecef;
}

.ticketStub::before {
  top: 18px;
}

.ticketStub::after {
  bottom: 18px;
}

.stubNoPoster {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #868e96;
  font-family: "NanumSquareRoundB", sans-serif;
}

.stubInfoOverlay {
  position: absolute;
  bottom: 0px;
  width: 100%;
  /* 아래쪽이 잘려 보이도록 */
  padding: 10px 8px;
  background: linear-gradient(135deg,
      rgba(255, 255, 255, 0.96) 0%,
      rgba(248, 251, 255, 0.98) 45%,
      rgba(232, 246, 255, 0.9) 100%);
  background-size: 200% 200%;
  animation: stubGlow 4.5s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

@keyframes stubGlow {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stubInfoOverlay {
    animation: none;
  }
}

.stubLabel {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 10px;
  letter-spacing: 1.2px;
  color: #2b8bd6;
}

.stubQty {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 16px;
  color: #333;
}

.ticketBody {
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ticketHeaderRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.titleRow {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.posterThumb {
  width: 44px;
  height: 62px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  background: #f1f3f5;
  flex-shrink: 0;
}

.posterImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.posterFallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #868e96;
  font-family: "NanumSquareRoundB", sans-serif;
}

.ticketInfoRow {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ticketFooterRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 2px;
}

.ticketNo {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  color: #868e96;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

.liveTitle {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 15px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.metaText {
  font-size: 12px;
  color: #868e96;
  font-family: "NanumSquareRoundR", sans-serif;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dot {
  color: #c0c4c7;
}

.badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.refundExpectedRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 8px;
  background: rgba(102, 189, 255, 0.09);
  border: 1px solid rgba(102, 189, 255, 0.22);
}

.refundExpectedLabel {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 11px;
  color: #1a6fa8;
  letter-spacing: -0.02em;
}

.refundExpectedValue {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  color: #1565a8;
  white-space: nowrap;
}

.statusBadge {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  border: 1px solid transparent;
  white-space: nowrap;
}

.pending {
  background: #fff9e6;
  color: #996c00;
  border-color: #ffe8a1;
}

.confirmed {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #a5d6a7;
}

.cancelled {
  background: #fce4ec;
  color: #c62828;
  border-color: #f8bbd0;
}

.refund_requested {
  background: rgba(102, 189, 255, 0.1);
  color: #1a6fa8;
  border-color: rgba(102, 189, 255, 0.4);
}

.refund_completed {
  background: #e8eaf6;
  color: #3949ab;
  border-color: #9fa8da;
}

.pay_confirmed {
  background: rgba(102, 187, 106, 0.12);
  color: #2e7d32;
  border-color: rgba(102, 187, 106, 0.35);
}

.pay_pending {
  background: #f8f9fa;
  color: #868e96;
  border-color: #e9ecef;
}

.payBadge {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  border: 1px solid #e9ecef;
  white-space: nowrap;
}

.payPending {
  background: #f8f9fa;
  color: #868e96;
}

.payConfirmed {
  background: rgba(102, 187, 106, 0.12);
  color: #2e7d32;
  border-color: rgba(102, 187, 106, 0.35);
}

.inviteBadge {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.12) 0%, rgba(91, 33, 182, 0.12) 100%);
  color: #6d28d9;
  border: 1px solid rgba(124, 58, 237, 0.3);
  white-space: nowrap;
}

.inviteTicketNotice {
  background: #f5f0ff;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #6d28d9;
  margin-bottom: 16px;
  text-align: center;
}

.expandIcon {
  font-size: 11px;
  color: #66bdff;
  min-width: 18px;
  text-align: center;
}

.detail {
  border-top: 1px solid #e9ecef;
  padding: 16px;
  background: #fff;
}

.detailRow {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
}

.detailRow:last-child {
  border-bottom: none;
}

.detailLabel {
  min-width: 90px;
  font-size: 12px;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.detailValue {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #6c757d;
  font-family: "NanumSquareRoundR", sans-serif;
  overflow-wrap: anywhere;
}

.refundExpectedDetailValue {
  font-family: "NanumSquareRoundEB", sans-serif;
  color: #1565a8;
}

.refundExpectedHint {
  margin: -4px 0 10px 0;
  padding: 0;
  font-size: 11px;
  line-height: 1.45;
  color: #868e96;
  font-family: "NanumSquareRoundR", sans-serif;
}

.linkButton {
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fff;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  color: #2b8bd6;
}

.linkButton:hover {
  border-color: #66bdff;
  background: rgba(102, 189, 255, 0.12);
}

.detailActions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 0 0;
  margin-top: 6px;
  border-top: 1px solid #f1f3f5;
}

.cancelButton {
  width: min(360px, 100%);
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(239, 83, 80, 0.55);
  background: rgba(239, 83, 80, 0.08);
  color: #c62828;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
}

.cancelButton:hover:not(:disabled) {
  background: rgba(239, 83, 80, 0.12);
  border-color: rgba(239, 83, 80, 0.75);
}

.cancelButton:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.refundButton {
  width: min(360px, 100%);
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(102, 189, 255, 0.55);
  background: rgba(102, 189, 255, 0.08);
  color: #1a6fa8;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
}

.refundButton:hover:not(:disabled) {
  background: rgba(102, 189, 255, 0.14);
  border-color: rgba(102, 189, 255, 0.75);
}

.refundButton:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.refundRequestedBadge {
  width: min(360px, 100%);
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid rgba(102, 189, 255, 0.35);
  background: rgba(102, 189, 255, 0.06);
  color: #2b8bd6;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  text-align: center;
}

.statusBadgeRow {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.cancelledBadge {
  width: min(360px, 100%);
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid rgba(239, 83, 80, 0.25);
  background: rgba(239, 83, 80, 0.05);
  color: #c62828;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  text-align: center;
}

.refundCompletedBadge {
  width: min(360px, 100%);
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(57, 73, 171, 0.3);
  background: linear-gradient(135deg, rgba(57, 73, 171, 0.06) 0%, rgba(121, 134, 203, 0.08) 100%);
  color: #3949ab;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  text-align: center;
  letter-spacing: 0.5px;
}

/* 환불 계좌 입력 폼 */
.refundForm {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(102, 189, 255, 0.35);
  background: rgba(102, 189, 255, 0.04);
}

.refundFormTitle {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #2b8bd6;
  margin: 0 0 4px 0;
}

.refundFormNotice {
  margin: 0 0 10px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(43, 139, 214, 0.15);
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  line-height: 1.5;
  color: #495057;
  white-space: pre-line;
}

.refundFormRow {
  display: flex;
  align-items: center;
  gap: 10px;
}

.refundFormLabel {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  color: #495057;
  min-width: 52px;
  flex-shrink: 0;
}

.refundFormInput {
  flex: 1;
  min-width: 0;
  padding: 9px 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}

.refundFormInput:focus {
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.12);
}

.refundFormInput:disabled {
  background: #f7fafc;
  cursor: not-allowed;
  opacity: 0.6;
}

.refundFormActions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.refundFormCancelBtn {
  flex: 1;
  padding: 10px 0;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  color: #868e96;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  cursor: pointer;
}

.refundFormCancelBtn:hover:not(:disabled) {
  background: #f8f9fa;
}

.refundFormSubmitBtn {
  flex: 2;
  padding: 10px 0;
  border-radius: 8px;
  border: 1px solid rgba(102, 189, 255, 0.55);
  background: rgba(102, 189, 255, 0.1);
  color: #1a6fa8;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.refundFormSubmitBtn:hover:not(:disabled) {
  background: rgba(102, 189, 255, 0.18);
}

.refundFormSubmitBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.goodsOption {
  font-size: 12px;
  color: #868e96;
  font-family: "NanumSquareRoundR", sans-serif;
}

.detailRowTotal {
  border-top: 1px solid #e9ecef;
  margin-top: 4px;
  padding-top: 12px;
}

.detailValueTotal {
  font-family: "NanumSquareRoundB", sans-serif;
  color: #1a6fa8;
}

.cancelHint {
  font-size: 12px;
  color: #868e96;
  font-family: "NanumSquareRoundR", sans-serif;
  text-align: center;
}

.dividerTitle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 14px 0 8px;
}

.dividerTitle::before,
.dividerTitle::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e9ecef;
}

.dividerTitle span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #e9ecef;
  background: #fff;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.qrBox {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 12px;
  border-radius: 12px;
  background: #fff;
  margin-bottom: 14px;
}

.qrImage {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #fff;
}

.qrLoading {
  font-size: 13px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.qrError {
  font-size: 13px;
  color: #c62828;
  font-family: "NanumSquareRoundB", sans-serif;
}

.qrLinkButton {
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(102, 189, 255, 0.55);
  background: rgba(102, 189, 255, 0.12);
  color: #2b8bd6;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
}

.qrLinkButton:hover {
  background: rgba(102, 189, 255, 0.18);
  border-color: rgba(102, 189, 255, 0.75);
}

.qrHint {
  font-size: 12px;
  color: #868e96;
  font-family: "NanumSquareRoundR", sans-serif;
  text-align: center;
}

.emptyState {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.emptyIcon {
  font-size: 48px;
  margin-bottom: 16px;
}

.emptyState h3 {
  margin: 0 0 10px 0;
  font-size: 1.1rem;
  color: #333;
}

.emptyState p {
  margin: 0;
  font-size: 0.9rem;
}

@media screen and (max-width: 480px) {
  .container {
    width: 100%;
  }

  .paper {
    padding: 12px;
  }

  .ticketStub {
    width: 92px;
  }

  .stubPoster {
    min-height: 136px;
  }

  .stubInfoOverlay {
    padding: 9px 6px;
    gap: 5px;
  }

  .stubLabel {
    font-size: 9px;
    letter-spacing: 1px;
  }

  .stubQty {
    font-size: 14px;
  }

  .ticketBody {
    padding: 12px 14px;
  }

  .posterThumb {
    width: 40px;
    height: 56px;
    border-radius: 8px;
  }

  .badges {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .detailLabel {
    min-width: auto;
  }

  .detailRow {
    flex-direction: column;
    gap: 6px;
  }

  .qrImage {
    width: 180px;
    height: 180px;
  }

  .cancelButton {
    width: 100%;
  }

  .refundButton {
    width: 100%;
  }

  .refundRequestedBadge {
    width: 100%;
  }

  .cancelledBadge {
    width: 100%;
  }

  .refundFormRow {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  .refundFormLabel {
    min-width: unset;
  }

  .refundFormInput {
    width: 100%;
  }
}
</style>
